import { Box, Link, Typography } from "@mui/material";
import { ReactNode } from "react";
import { sanitizeHighlight } from "../../utils/sanitize-html";

/**
 * The profile header box (name + headline + location), shared by the person
 * profile (PersonSummary) and the parsed-CV candidate card so they're identical.
 * ODI-only extras (links, badges, location tooltip) are passed in as slots.
 */
export const ProfileHeader = ({
	name,
	onNameClick,
	href,
	afterName,
	headline,
	headlineHtml,
	location,
	children,
}: {
	name: string;
	/** Render the name as a button firing this (e.g. open a modal). */
	onNameClick?: () => void;
	/** Render the name as a link to this href (when no onNameClick). */
	href?: string;
	/** Inline content after the name (badges, external links). */
	afterName?: ReactNode;
	/** Plain-text headline (candidate). */
	headline?: string;
	/** HTML headline (ODI person — rendered as markup). */
	headlineHtml?: string;
	/** Location element (ODI wraps it in a tooltip; candidate passes plain text). */
	location?: ReactNode;
	/** Extra content below (e.g. candidate contact). */
	children?: ReactNode;
}) => (
	<Box sx={{ p: 2 }} data-component="person-header-box">
		{onNameClick ? (
			<Link
				component="button"
				type="button"
				onClick={onNameClick}
				variant="h6"
				underline="hover"
				sx={{
					fontWeight: 700,
					textAlign: "left",
					verticalAlign: "baseline",
				}}
				data-component="person-name-link"
			>
				{name}
			</Link>
		) : href ? (
			<Link
				href={href}
				target="_blank"
				variant="h6"
				underline="hover"
				sx={{ fontWeight: 700 }}
				data-component="person-name-link"
			>
				{name}
			</Link>
		) : (
			<Typography
				variant="h6"
				component="span"
				sx={{ fontWeight: 700 }}
				data-component="person-name-link"
			>
				{name}
			</Typography>
		)}
		{afterName}
		{headlineHtml ? (
			<Typography
				variant="body2"
				color="text.primary"
				sx={{ mt: 0.75, lineHeight: 1.5 }}
				data-component="person-headline"
				dangerouslySetInnerHTML={{ __html: sanitizeHighlight(headlineHtml) }}
			/>
		) : headline ? (
			<Typography
				variant="body2"
				color="text.primary"
				sx={{ mt: 0.75, lineHeight: 1.5 }}
				data-component="person-headline"
			>
				{headline}
			</Typography>
		) : null}
		{location}
		{children}
	</Box>
);
